import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useState } from 'react'

import {
  useClassifierServiceDeleteTreatmentRegimenTypeEndpoint,
  useClassifierServiceSearchTreatmentRegimenTypeEndpoint,
  useClassifierServiceSearchTreatmentRegimenTypeEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  TreatmentRegimenTypeDto
} from 'src/api/requests'
import {
  ClassifierInfiniteSelect,
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler, resetPagination } from 'src/utils'

import { TreatmentTypesClassifierModal } from '../components'

export const TreatmentTypesClassifier = () => {
  const { t, currentLang } = useLocales()
  const queryClient = useQueryClient()

  const pagination = useTablePagination()
  const [search, setSearch] = useState<string>()
  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()
  const [typeId, setTypeId] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!typeId && {
      typeIds: [typeId]
    })
  }

  const { data, isLoading } =
    useClassifierServiceSearchTreatmentRegimenTypeEndpoint(queryParams, [
      queryParams
    ])

  const { mutate } = useClassifierServiceDeleteTreatmentRegimenTypeEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useClassifierServiceSearchTreatmentRegimenTypeEndpointKey]
      })
      message.success(t(''))
    }
  })

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'edit',
      label: t('common.edit'),
      icon: <EditOutlined />,
      onClick: () => {
        setCurrentId(id)
        setIsOpen(true)
      }
    },
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('treatment_types_classifier.delete_treatment_type_title')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnType<TreatmentRegimenTypeDto>[] = [
    {
      key: 'order',
      title: t('common.order'),
      dataIndex: 'order',
      sorter: (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
    },
    {
      key: 'name',
      title: t('classifiers.title'),
      render: (_, { translation }) => translation?.[currentLang?.value]
    },
    {
      key: 'typeId',
      title: t('treatment_types_classifier.treatment_type'),
      render: (_, { type }) => type?.translation?.[currentLang?.value]
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  const onTypeIdChange = (val: string) => {
    setTypeId(val)
    resetPagination(pagination)
  }

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onModalClose = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const treatmentTypes = useClassifierId(
    ClassifierType.TREATMENT_TYPE,
    ClassifierCategory.TREATMENT
  )

  return (
    <PageBreadcrumbs
      title={t('menu.treatment_types_classifier')}
      items={[{ title: t('menu.treatment_types_classifier') }]}
    >
      <Flex vertical gap={24}>
        <Flex gap={24}>
          <SearchBar
            className="full-width"
            placeholder={t('treatment_types_classifier.search_placeholder')}
            onSearch={onSearch}
          />

          <ClassifierInfiniteSelect
            value={typeId}
            hasDefault
            onChange={onTypeIdChange}
            id={treatmentTypes}
            placeholder={t('treatment_types_classifier.treatment_type')}
          />

          <Button icon={<PlusOutlined />} type="primary" onClick={onToggle}>
            {t('treatment_types_classifier.create_treatment_type')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && (
        <TreatmentTypesClassifierModal onCancel={onModalClose} id={currentId} />
      )}
    </PageBreadcrumbs>
  )
}
